import React, { useState } from "react"
import { CaretDownOutlined, LoadingOutlined, PaperClipOutlined } from "@ant-design/icons"
import { InboxInvoice, OcrStatusEnum, PriceIntervalEnum } from "@finway-group/shared/lib/models"
import { DocumentTypeEnum, UploadKindEnum } from "@finway-group/shared/lib/models"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { Button, Dropdown, Menu, Table, Tag } from "antd"
import { ColumnsType } from "antd/lib/table"
import moment from "moment"
import { useTranslation } from "react-i18next"
// redux
import { useDispatch } from "react-redux"

import { CircleArrowIcon, EyeNoBgIcon, TrashIcon } from "Components/icons"
import Loading from "Components/loading"
import InboxInvoiceModal from "Components/modals/inboxInvoice/inboxInvoice.modal"
import OcrStatusBadge from "Components/ocrStatusBadge"
import UploadFilesDragger from "Components/upload/UploadFilesDragger"
import { checkForDuplicateInvoiceFile, getExpensesDocsNeeded } from "Features/pages/expenses/expenseUtils"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { FileService, NotificationService, UserService } from "Shared/services"
import InboxInvoiceService from "Shared/services/inboxInvoice.service"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { createInboxInvoice, discardInboxInvoice, fetchDashboardInboxInvoicesCount } from "Shared/store/actions/inboxInvoice/inboxInvoiceActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { checkFilesSize, isNotSet } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { useTableFilterAndSearchLoading, useFetchTable, useTable, useTableSearchString } from "Shared/hooks/table.hooks"
import { getSortOrderForColumn } from "Shared/utils/table.utils"
import { refetchTable, updateTableFilter, updateTableOnChange, updateTableSearch } from "Shared/store/actions/tables/tableActions"
import { ErrorTable } from "Components/layout/errorTable"
import { useExpenses } from "Shared/hooks/expense.hooks"
import { Download as DownloadIcon } from "react-feather"
import fileDownload from "js-file-download"
import InboxFilterModal from "Components/modals/inboxFilter.modal"
import TableButtons from "Components/tableButtons"
import EmptyTableData from "Components/emptyTableData"
import { useSelectedInboxInvoices } from "Shared/context/selectedExpenses.context"
import { getPopupAnchor } from "Shared/utils"
import PriceLabel from "Components/priceLabel"

const enum InboxInvoiceOptionsEnum {
    REMOVE,
    PREVIEW,
    DOWNLOAD,
    RETRY_OCR,
}

interface InboxInvoiceTabInterface {}

const InboxInvoiceTab: React.FC<InboxInvoiceTabInterface> = ({}: InboxInvoiceTabInterface) => {
    const { expenses } = useExpenses(true)
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [isUploading, setIsUploading] = useState(false)
    const tabLoading = useTableFilterAndSearchLoading(TablesEnum.INBOX_INVOICE)
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const [isInvoiceInboxLoading, setIsInvoiceInboxLoading] = useStateIfMounted(false)
    const docsNeeded = getExpensesDocsNeeded(expenses)
    const [selectedInvoice, setSelectedInvoice] = useStateIfMounted<InboxInvoice | undefined>(undefined)
    const [isShowingModal, setIsShowingModal] = useState(false)
    const inboxInvoiceTable = useTable<InboxInvoice>(TablesEnum.INBOX_INVOICE)
    const searchString = useTableSearchString(TablesEnum.INBOX_INVOICE)
    const [selectedInboxInvoices, setSelectedInboxInvoices] = useSelectedInboxInvoices()

    const isFilterFilled = () => {
        const filterObject = inboxInvoiceTable.data.filter
        return Object.values(filterObject).some((val) => !isNotSet(val))
    }

    useFetchTable(TablesEnum.INBOX_INVOICE)

    // prettier-ignore
    const onSelect = (inboxInvoice: InboxInvoice) => async ({ key, domEvent }: any) => {
        domEvent.stopPropagation();

        switch (Number(key)) {
            case InboxInvoiceOptionsEnum.REMOVE:
                setIsInvoiceInboxLoading(true)
                discardInboxInvoice(inboxInvoice)(dispatch)
                    .then(() => NotificationService.send(NotificationTypeEnum.SUCCESS, t('notification:invoice_inbox.deleted.title'), t('notification:invoice_inbox.deleted.message')))
                    .catch((err) => NotificationService.showErrorNotificationBasedOnResponseError(err, t('error:invoice_inbox.delete.titlle')))
                    .finally(() => setIsInvoiceInboxLoading(false))
                break;
            case InboxInvoiceOptionsEnum.PREVIEW:
                openPreview(inboxInvoice);
                break;
            case InboxInvoiceOptionsEnum.DOWNLOAD:
                const { data } = await FileService.downloadFile(inboxInvoice.url)
                fileDownload(data, `${inboxInvoice.fileName}.pdf`)
                break;
            case InboxInvoiceOptionsEnum.RETRY_OCR:
                InboxInvoiceService.retryInboxInvoiceOcr(inboxInvoice.id);
                break;
            default:
                break;
        }
    };

    const dropDownOverlay = (InboxInvoice: InboxInvoice) => () => {
        return (
            <Menu onClick={onSelect(InboxInvoice)}>
                {InboxInvoice.ocrStatus === OcrStatusEnum.FAILED ? (
                    <Menu.Item key={InboxInvoiceOptionsEnum.RETRY_OCR} className="flex items-center min-w-190 py-8">
                        <div className="flex items-center">
                            <CircleArrowIcon size={14} />
                            <p className="leading-none">
                                <span className="text-text ml-3">{t("action:inbox_invoice.retry_ocr")}</span>
                            </p>
                        </div>
                    </Menu.Item>
                ) : null}
                <Menu.Item key={InboxInvoiceOptionsEnum.PREVIEW} className="flex items-center min-w-190 py-8">
                    <div className="flex items-center">
                        <p className="leading-none">
                            <EyeNoBgIcon size={14} />
                            <span className="text-text"> {t("action:inbox_invoice.preview")}</span>
                        </p>
                    </div>
                </Menu.Item>
                <Menu.Item key={InboxInvoiceOptionsEnum.DOWNLOAD} className="flex items-center min-w-190 py-8">
                    <div className="flex items-center">
                        <DownloadIcon size={14} />
                        <p className="leading-none ml-4">
                            <span className="text-text"> {t("action:inbox_invoice.download")}</span>
                        </p>
                    </div>
                </Menu.Item>
                <Menu.Item key={InboxInvoiceOptionsEnum.REMOVE} className="flex items-center min-w-190 py-8">
                    <div className="flex items-center">
                        <TrashIcon size={14} />
                        <p className="leading-none">
                            <span className="text-text ml-5">{t("action:inbox_invoice.delete")}</span>
                        </p>
                    </div>
                </Menu.Item>
            </Menu>
        )
    }

    const openPreview = (inboxInvoice: InboxInvoice) => {
        setSelectedInvoice(inboxInvoice)
        setIsShowingModal(true)
    }

    const columns: ColumnsType<InboxInvoice> = [
        {
            title: t("info:inbox_invoice.file_name"),
            key: "fileName",
            align: "left",
            className: "pl-10",
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "fileName"),
            render: ({ fileName, duplicate }: InboxInvoice) => (
                <div className="flex flex-wrap">
                    <p className="truncate">{fileName}</p>
                    {duplicate.hash || duplicate.invoiceNumber ? <Tag className="ant-tag-red ml-10">{t("label:potential_duplicate")} </Tag> : <></>}
                </div>
            ),
        },
        {
            title: t("info:inbox_invoice.upload_date"),
            key: "uploadDate",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "uploadDate"),
            render: ({ uploadDate }: InboxInvoice) => <div>{moment(uploadDate).format("ll")}</div>,
        },
        {
            title: t("info:inbox_invoice.ocr"),
            key: "ocrStatus",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "ocrStatus"),
            render: ({ ocrStatus }: InboxInvoice) => <OcrStatusBadge ocrStatus={ocrStatus} />,
        },
        {
            title: t("info:inbox_invoice.vendor"),
            key: "ocrResults.vendorData.name",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "ocrResults.vendorData.name"),
            render: ({ ocrResults }: any) => <div>{ocrResults?.vendorData?.name || "n/a"} </div>,
        },
        {
            title: t("info:inbox_invoice.uploaded_by"),
            key: "uploadedByObject.firstName",
            width: 120,
            align: "left",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "uploadedByObject.firstName"),
            render: ({ uploadedBy }: InboxInvoice) => {
                const employee = GetEmployeeById(useEmployees(), uploadedBy)
                return (
                    <div>
                        {employee?.firstName} {employee?.lastName}
                    </div>
                )
            },
        },
        {
            title: t("info:inbox_invoice.gross_amount"),
            key: "ocrResults.invoiceData.grossAmount",
            width: 120,
            align: "right",
            sorter: true,
            sortOrder: getSortOrderForColumn(inboxInvoiceTable, "ocrResults.invoiceData.grossAmount"),
            render: ({ ocrResults }: any) =>
                ocrResults?.invoiceData?.grossAmount ? (
                    <PriceLabel value={ocrResults?.invoiceData?.grossAmount} currency={ocrResults?.invoiceData?.currency} interval={PriceIntervalEnum.ONE_TIME} />
                ) : (
                    <div>{"n/a"} </div>
                ),
        },
        {
            title: t("info:inbox_invoice.action"),
            width: 120,
            align: "right",
            className: "pr-10",
            render: (InboxInvoice) => (
                <div>
                    <Dropdown getPopupContainer={getPopupAnchor()} overlay={dropDownOverlay(InboxInvoice)} trigger={["click"]}>
                        <Button onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()} className={"m-5 ant-btn-primary"}>
                            <span>{t("label:options")} </span>
                            <CaretDownOutlined className="text-sm" />
                        </Button>
                    </Dropdown>
                </div>
            ),
        },
    ]

    const doFileUpload = async (files: File[]) => {
        if (!checkFilesSize(files)) return

        setIsUploading(true)
        try {
            const data = await FileService.uploadMultiple(files, UploadKindEnum.INBOX_INVOICE)
            for (const fileUploadData of data) {
                const createData = {
                    fileName: fileUploadData.originalname,
                    url: fileUploadData.url,
                    hash: fileUploadData.hash,
                    uploadDate: fileUploadData.createdAt,
                    uploadedBy: fileUploadData.createdBy,
                    duplicate: { hash: fileUploadData.duplicateData.isDuplicate },
                }
                if (await checkForDuplicateInvoiceFile(fileUploadData.duplicateData)) createInboxInvoice(createData)(dispatch)
            }
        } catch (err) {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:file.upload.title"), t("error:file.upload.message"))
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <>
            {tabLoading ? (
                <Loading />
            ) : (
                <>
                    <div className="animation-appear">
                        <UploadFilesDragger
                            doFileUpload={doFileUpload}
                            documentType={DocumentTypeEnum.INVOICE}
                            label={{
                                buttonLabel: t("action:add_invoice"),
                                draggerDescription: t("action:or_drop_files"),
                            }}
                            isUploading={isUploading}
                        />
                    </div>
                    {(!!inboxInvoiceTable.data.totalDocs || inboxInvoiceTable.data.search || isFilterFilled()) && (
                        <TableButtons
                            table={TablesEnum.INBOX_INVOICE}
                            onSearch={async (searchString: string) => {
                                setIsInvoiceInboxLoading(true)
                                await dispatch(updateTableSearch(TablesEnum.INBOX_INVOICE, searchString))
                                await dispatch(fetchDashboardInboxInvoicesCount())
                                setIsInvoiceInboxLoading(false)
                            }}
                            injectFilterModal={(isVisible: boolean, onCancel: any) => (
                                <InboxFilterModal
                                    isVisible={isVisible}
                                    onCancel={onCancel}
                                    onSubmit={async (filterFormData: any) => {
                                        setIsInvoiceInboxLoading(true)
                                        await dispatch(updateTableFilter(TablesEnum.INBOX_INVOICE, filterFormData))
                                        await dispatch(fetchDashboardInboxInvoicesCount())
                                        setIsInvoiceInboxLoading(false)
                                    }}
                                />
                            )}
                            setIsShowingModal={setIsShowingModal}
                            additionalButtons={
                                <Button onClick={() => setIsShowingModal(true)}>
                                    <PaperClipOutlined />
                                    {t("action:inbox_invoice.attach_to_request")}
                                </Button>
                            }
                        />
                    )}

                    {inboxInvoiceTable.error ? (
                        <ErrorTable onTableReload={() => dispatch(refetchTable(TablesEnum.INBOX_INVOICE))} isLoading={inboxInvoiceTable.isFetching} />
                    ) : inboxInvoiceTable.data.totalDocs === 0 && !inboxInvoiceTable.isFetching ? (
                        <EmptyTableData searchString={searchString} title={t("info:empty_state.inbox.title")} message={t("info:empty_state.inbox.message")} />
                    ) : (
                        <div className="overflow-auto mb-24 mt-20 p'2 animation-appear">
                            <Table
                                rowKey={(record) => record.id}
                                dataSource={inboxInvoiceTable.data.docs}
                                columns={columns}
                                onChange={updateTableOnChange(TablesEnum.INBOX_INVOICE, inboxInvoiceTable.data)}
                                loading={{
                                    spinning: inboxInvoiceTable.isFetching || isInvoiceInboxLoading,
                                    indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                                }}
                                rowSelection={{
                                    type: "checkbox",
                                    renderCell: (_value, _record, _index, originNode) => (
                                        <div onClick={(e) => e.stopPropagation()} className="bigger-hitbox-checkbox">
                                            {originNode}
                                        </div>
                                    ),
                                    selectedRowKeys: selectedInboxInvoices.map((record) => record.id),
                                    onChange: (_selectedRowKeys, selectedRows) => setSelectedInboxInvoices(selectedRows),
                                    onSelect: (record, _selected, _selectedRow) => setSelectedInboxInvoices([...selectedInboxInvoices, record]),
                                }}
                                pagination={{
                                    position: ["bottomRight"],
                                    showSizeChanger: true,
                                    current: inboxInvoiceTable.data.page,
                                    pageSize: inboxInvoiceTable.data.limit,
                                    hideOnSinglePage: false,
                                    total: inboxInvoiceTable.data.totalDocs,
                                    pageSizeOptions: ["5", "10", "20", "50", "100"],
                                }}
                                onRow={(record, _rowIndex) => {
                                    return {
                                        onClick: (_event) => {
                                            openPreview(record)
                                        },
                                    }
                                }}
                            />
                        </div>
                    )}

                    {isShowingModal && (
                        <InboxInvoiceModal
                            isShowing={isShowingModal}
                            onCancel={() => {
                                setIsShowingModal(false)
                                setSelectedInvoice(undefined)
                            }}
                            expenses={docsNeeded}
                            preSelectedInvoice={selectedInvoice}
                            shouldResetSelectedExpenseOnClose
                            shouldResetSelectedInboxOnClose
                            isOcrEnabled={loggedInUser.settings.enableOcr}
                        />
                    )}
                </>
            )}
        </>
    )
}

export default InboxInvoiceTab
